import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  Save,
  Trash2,
  Search,
  Download,
  HardHat,
  Circle,
  Building2,
  DollarSign,
  Pencil,
  X,
  User as UserIcon,
  UserPlus,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import { api } from "../../lib/api";
import { constructionApi } from "../../lib/copApi";
import { contactsApi } from "../../lib/contactsApi";
import { useAuthStore } from "../../store/authStore";
import { nameInitials } from "../../lib/nameInitials";
import AddContactDialog from "../../components/contacts/AddContactDialog";

// Predefined enum choices sourced from the user's Smartsheet screenshots.
// Backend stores these as free-form strings so tenants can extend later.
const HUB_OPTIONS = [
  "Technology Innovation",
  "Climate Finance",
  "Resilient and Adaptive",
  "Energy Transition",
  "Technology Innovation Knowledge",
  "Other",
];
const STAND_TYPES = [
  "Custom Stand",
  "Modular Stand",
  "Shell Scheme",
  "Package Stand",
  "Other",
];
const STAGE_OPTIONS = [
  "Introduction",
  "Proposal Sent",
  "Negotiation",
  "Contract Signed",
  "In Production",
  "Delivered",
  "Cancelled",
];
const STATUS_META = {
  green: { color: "#059669", label: "Aktif" },
  yellow: { color: "#f59e0b", label: "Beklemede" },
  red: { color: "#dc2626", label: "Sorunlu" },
  gray: { color: "#9ca3af", label: "Belirsiz" },
};
const CURRENCIES = ["USD", "EUR", "GBP", "TRY"];

// Format currency like "1.000" for TR readability. Same helper Deals uses.
const fmtMoney = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(v);
};

// Live "type-as-you-go" budget formatter. Users see "250.000" while typing but
// state holds the raw numeric string ("250000") so the backend receives a
// plain number without any locale-specific separators.
const parseMoneyDigits = (raw) => (raw || "").toString().replace(/[^\d]/g, "");
const formatMoneyDisplay = (raw) => {
  const digits = parseMoneyDigits(raw);
  if (!digits) return "";
  return new Intl.NumberFormat("tr-TR").format(Number(digits));
};

// Searchable, alphabetically-sorted company picker. Prefix matches rank
// first (Turkish locale), substring matches follow. Popover is start-aligned
// and clamped to the trigger width so it never bleeds outside the sheet.
function CompanyCombobox({ companies, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const sorted = useMemo(
    () =>
      [...companies].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "tr", { sensitivity: "base" }),
      ),
    [companies],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr");
    if (!q) return sorted;
    const starts = [];
    const contains = [];
    for (const c of sorted) {
      const n = (c.name || "").toLocaleLowerCase("tr");
      if (n.startsWith(q)) starts.push(c);
      else if (n.includes(q)) contains.push(c);
    }
    return [...starts, ...contains];
  }, [sorted, query]);

  const selected = value ? sorted.find((c) => c.id === value) : null;

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="construction-form-company-select"
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selected ? selected.name : "Kontaklardan seç veya boş bırak"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Firma ara..."
            value={query}
            onValueChange={setQuery}
            data-testid="construction-company-search"
          />
          <CommandList className="max-h-64">
            <CommandEmpty>Sonuç bulunamadı</CommandEmpty>
            <CommandItem
              value="__none__"
              onSelect={() => pick("__none__")}
              data-testid="construction-company-none"
            >
              <Check
                className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`}
              />
              — Firma seçilmedi (manuel gireceğim) —
            </CommandItem>
            {filtered.map((c) => (
              <CommandItem
                key={c.id}
                value={c.id}
                onSelect={() => pick(c.id)}
              >
                <Check
                  className={`mr-2 h-4 w-4 ${value === c.id ? "opacity-100" : "opacity-0"}`}
                />
                <span className="truncate">{c.name}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const EMPTY_ROW = {
  company_id: null,
  company_name: "",
  country: "Turkey",
  hub_name: "",
  stand_no: "",
  contact_id: null,
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  assigned_to_user_id: null,
  status: "gray",
  stand_type: "",
  height_cm: "",
  size_m2: "",
  stand_cost: "",
  currency: "USD",
  stage: "",
  notes: "",
};

function StatusDot({ status, size = "sm" }) {
  const meta = STATUS_META[status] || STATUS_META.gray;
  const px = size === "lg" ? 16 : 10;
  return (
    <span
      title={meta.label}
      style={{ backgroundColor: meta.color, width: px, height: px }}
      className="inline-block rounded-full ring-2 ring-white"
      data-testid={`construction-status-dot-${status}`}
    />
  );
}

function KpiCard({ icon: Icon, label, value, tint = "zinc" }) {
  const tints = {
    zinc: "bg-zinc-50 text-zinc-900",
    green: "bg-emerald-50 text-emerald-900",
    yellow: "bg-amber-50 text-amber-900",
    red: "bg-red-50 text-red-900",
    violet: "bg-violet-50 text-violet-900",
    sky: "bg-sky-50 text-sky-900",
  };
  return (
    <div className={`rounded-xl border border-zinc-200 p-4 ${tints[tint]}`}>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider opacity-70">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

export default function CopConstruction() {
  const { user } = useAuthStore();
  const isSuper = ["super_admin", "ultra_admin"].includes(user?.role);

  // Master state
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [persons, setPersons] = useState([]);
  const [users, setUsers] = useState([]);
  const [managedHubs, setManagedHubs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [q, setQ] = useState("");
  const [hubFilter, setHubFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const [assignedFilter, setAssignedFilter] = useState("");

  // Drawer state (edit or create)
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_ROW });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  // "+ Yeni Kişi" inline flow — opens a nested AddContactDialog pinned to
  // the current company so the new contact lands linked in one save.
  const [addPersonOpen, setAddPersonOpen] = useState(false);

  // Load initial data
  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (hubFilter) params.hub_name = hubFilter;
      if (stageFilter) params.stage = stageFilter;
      if (statusFilter) params.status = statusFilter;
      if (mineOnly) params.mine_only = "true";
      if (assignedFilter) params.assigned_to_user_id = assignedFilter;
      const data = await constructionApi.list(params);
      setRows(data);
    } catch (err) {
      toast.error("Kayıtlar yüklenemedi");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [q, hubFilter, stageFilter, statusFilter, mineOnly, assignedFilter]);

  const loadSummary = useCallback(async () => {
    try {
      const s = await constructionApi.summary();
      setSummary(s);
    } catch (err) {
      // Non-blocking; the KPI strip is just decorative.
      console.error(err);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [companyData, personData, userData, hubData] = await Promise.all([
          contactsApi.list({ kind: "company" }),
          contactsApi.list({ kind: "person" }),
          api.get("/users?assignable=true").then((r) => r.data),
          api.get("/hubs").then((r) => r.data).catch(() => []),
        ]);
        setCompanies(companyData || []);
        setPersons(personData || []);
        setUsers(userData || []);
        setManagedHubs(hubData || []);
      } catch {
        // Non-blocking; user can still type company names manually.
      }
    })();
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, rows.length]);

  // Company lookup — snapshot chosen company's name into form on select.
  const companyById = useMemo(() => {
    const map = {};
    for (const c of companies) map[c.id] = c;
    return map;
  }, [companies]);
  const userById = useMemo(() => {
    const map = {};
    for (const u of users) map[u.id] = u;
    return map;
  }, [users]);
  const personById = useMemo(() => {
    const map = {};
    for (const p of persons) map[p.id] = p;
    return map;
  }, [persons]);

  // Hub Yönetimi hub'ları + sabit liste + mevcut kayıtlardaki serbest değerler
  // tek dropdown listesinde birleşir (normalize ederek çift kayıt engellenir).
  const hubOptions = useMemo(() => {
    const norm = (s) => (s || "").toLocaleLowerCase("tr").replace(/[^a-z0-9çğıöşü]+/g, "");
    const seen = new Set();
    const out = [];
    const push = (name) => {
      const k = norm(name);
      if (!k || seen.has(k)) return;
      seen.add(k);
      out.push(name);
    };
    for (const h of managedHubs) push(h.name);
    for (const h of HUB_OPTIONS) push(h);
    for (const r of rows) push(r.hub_name);
    if (form.hub_name) push(form.hub_name);
    return out;
  }, [managedHubs, rows, form.hub_name]);

  // Persons available for the currently-selected company. If no company is
  // picked yet, show every person so users can still work manually.
  const personsForCurrentCompany = useMemo(() => {
    if (!form.company_id) return persons;
    return persons.filter((p) => p.company_id === form.company_id);
  }, [persons, form.company_id]);

  // Prefer the primary email/phone if the contact record has arrays; the
  // contacts collection currently stores singular `email`/`phone` strings
  // but we defensively handle either shape.
  const pickPrimary = (val) => {
    if (val === null || val === undefined) return "";
    if (Array.isArray(val)) return val[0] || "";
    return String(val);
  };

  // For a contact of kind="person" the display name is derived from
  // first_name + last_name; fall back to `name` for legacy rows.
  const personDisplayName = (p) => {
    if (!p) return "";
    if (p.name) return p.name;
    const parts = [p.first_name, p.last_name].filter(Boolean);
    return parts.join(" ").trim();
  };

  const openCreate = () => {
    setEditingRow(null);
    setForm({ ...EMPTY_ROW });
    setSheetOpen(true);
  };
  const openEdit = (row) => {
    setEditingRow(row);
    setForm({
      ...EMPTY_ROW,
      ...row,
      height_cm: row.height_cm ?? "",
      size_m2: row.size_m2 ?? "",
      stand_cost: row.stand_cost ?? "",
      contact_id: row.contact_id || null,
    });
    setSheetOpen(true);
  };

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // E-posta derin bağlantısı: ?edit=<id> ile gelindiğinde ilgili kaydın
  // düzenleme modalını otomatik aç (sorumlu atama isteği mailleri buraya
  // magic link ile yönlendirir).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || !rows.length) return;
    const row = rows.find((r) => r.id === editId);
    if (row) {
      openEdit(row);
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // When a company is selected, snapshot its name/country AND — because the
  // person list is now scoped to this company — auto-pick the first person
  // if the company has exactly one contact, so the form fills instantly.
  const handleCompanyChange = (companyId) => {
    if (companyId === "__none__") {
      setForm((prev) => ({
        ...prev,
        company_id: null,
        contact_id: null,
        contact_name: "",
        contact_email: "",
        contact_phone: "",
      }));
      return;
    }
    const co = companyById[companyId];
    const linkedPersons = persons.filter((p) => p.company_id === companyId);
    const autoContact = linkedPersons.length === 1 ? linkedPersons[0] : null;
    setForm((prev) => ({
      ...prev,
      company_id: companyId,
      company_name: co?.name || prev.company_name,
      country: co?.country || prev.country,
      contact_id: autoContact?.id || null,
      contact_name: autoContact ? personDisplayName(autoContact) : "",
      contact_email: autoContact ? pickPrimary(autoContact.email || autoContact.emails) : "",
      contact_phone: autoContact ? pickPrimary(autoContact.phone || autoContact.phones) : "",
    }));
  };

  // Selecting a contact person auto-fills their email/phone.
  const handleContactChange = (contactId) => {
    if (contactId === "__none__") {
      setForm((prev) => ({
        ...prev,
        contact_id: null,
        contact_name: "",
        contact_email: "",
        contact_phone: "",
      }));
      return;
    }
    const p = personById[contactId];
    if (!p) return;
    setForm((prev) => ({
      ...prev,
      contact_id: contactId,
      contact_name: personDisplayName(p),
      contact_email: pickPrimary(p.email || p.emails),
      contact_phone: pickPrimary(p.phone || p.phones),
    }));
  };

  const submit = async () => {
    if (!form.company_name && !form.stand_no) {
      toast.error("En az firma adı veya stant no gerekli");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        company_id: form.company_id || null,
        company_name: form.company_name,
        country: form.country,
        hub_name: form.hub_name,
        stand_no: form.stand_no,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        assigned_to_user_id: form.assigned_to_user_id || null,
        status: form.status,
        stand_type: form.stand_type,
        stage: form.stage,
        currency: form.currency,
        notes: form.notes,
        // Numeric coercion — treat empty strings as null so the backend does
        // not receive "" for a Float field.
        height_cm: form.height_cm === "" ? null : Number(form.height_cm),
        size_m2: form.size_m2 === "" ? null : Number(form.size_m2),
        stand_cost:
          form.stand_cost === "" || form.stand_cost === null
            ? null
            : Number(parseMoneyDigits(form.stand_cost)),
      };
      if (editingRow) {
        await constructionApi.update(editingRow.id, payload);
        toast.success("Kayıt güncellendi");
      } else {
        await constructionApi.create(payload);
        toast.success("Kayıt oluşturuldu");
      }
      setSheetOpen(false);
      loadRows();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kayıt işlemi başarısız";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`"${row.company_name || row.stand_no}" kaydı silinsin mi?`)) return;
    setDeleting(row.id);
    try {
      await constructionApi.remove(row.id);
      toast.success("Kayıt silindi");
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      toast.error("Silme başarısız");
    } finally {
      setDeleting(null);
    }
  };

  const clearFilters = () => {
    setQ("");
    setHubFilter("");
    setStageFilter("");
    setStatusFilter("");
    setAssignedFilter("");
    setMineOnly(false);
  };

  const downloadCsv = async () => {
    try {
      // Mirror the visible filter set into the CSV request so what you see
      // is exactly what you export.
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      if (hubFilter) qs.set("hub_name", hubFilter);
      if (stageFilter) qs.set("stage", stageFilter);
      if (statusFilter) qs.set("status", statusFilter);
      if (assignedFilter) qs.set("assigned_to_user_id", assignedFilter);
      if (mineOnly) qs.set("mine_only", "true");
      const url = `${constructionApi.exportUrl()}${qs.toString() ? `?${qs}` : ""}`;
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `construction-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("CSV indirilemedi");
    }
  };

  return (
    <div className="mx-auto max-w-[1600px] p-4 sm:p-6 md:p-8" data-testid="cop-construction-page">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-100 to-orange-200">
              <HardHat className="h-5 w-5 text-amber-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">İnşaat / Stant Pipeline</h1>
              <p className="text-sm text-zinc-500">
                Etkinlik & fuar stant süreçleri — firmalar, temaslar, sözleşmeler.
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadCsv}
            data-testid="construction-export-csv"
          >
            <Download className="mr-1.5 h-4 w-4" />
            CSV indir
          </Button>
          <Button onClick={openCreate} data-testid="construction-add-btn">
            <Plus className="mr-1.5 h-4 w-4" />
            Yeni kayıt
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      {summary && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <KpiCard icon={HardHat} tint="violet" label="Toplam" value={summary.total} />
          <KpiCard icon={Circle} tint="green" label="Aktif" value={summary.status_green} />
          <KpiCard icon={Circle} tint="yellow" label="Beklemede" value={summary.status_yellow} />
          <KpiCard icon={Circle} tint="red" label="Sorunlu" value={summary.status_red} />
          <KpiCard icon={Building2} tint="sky" label="Firma sayısı" value={summary.unique_companies} />
          <KpiCard
            icon={DollarSign}
            tint="zinc"
            label="Toplam bütçe"
            value={fmtMoney(summary.total_cost)}
          />
        </div>
      )}

      {/* Filters bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-zinc-200 bg-white p-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Firma, kontak, stand no…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 pr-9"
            data-testid="construction-search-input"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              aria-label="Aramayı temizle"
              title="Aramayı temizle"
              data-testid="construction-search-clear"
              className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Select value={hubFilter || "__all__"} onValueChange={(v) => setHubFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-[190px]" data-testid="construction-filter-hub">
            <SelectValue placeholder="Hub" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tüm hublar</SelectItem>
            {hubOptions.map((h) => (
              <SelectItem key={h} value={h}>{h}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={stageFilter || "__all__"} onValueChange={(v) => setStageFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="construction-filter-stage">
            <SelectValue placeholder="Aşama" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tüm aşamalar</SelectItem>
            {STAGE_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-[140px]" data-testid="construction-filter-status">
            <SelectValue placeholder="Durum" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tüm durumlar</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => (
              <SelectItem key={k} value={k}>
                <span className="flex items-center gap-2">
                  <span
                    style={{ backgroundColor: m.color }}
                    className="inline-block h-2.5 w-2.5 rounded-full"
                  />
                  {m.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={assignedFilter || "__all__"}
          onValueChange={(v) => setAssignedFilter(v === "__all__" ? "" : v)}
        >
          <SelectTrigger className="w-[180px]" data-testid="construction-filter-assigned">
            <SelectValue placeholder="Sorumlu" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tüm sorumlular</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => setMineOnly((v) => !v)}
          data-testid="construction-mine-only-toggle"
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
            mineOnly
              ? "border-violet-300 bg-violet-50 text-violet-800"
              : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"
          }`}
        >
          {mineOnly ? "✓ " : ""}Sadece bana atanmış
        </button>
        {(q || hubFilter || stageFilter || statusFilter || mineOnly || assignedFilter) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            data-testid="construction-clear-filters"
          >
            <X className="mr-1 h-3.5 w-3.5" />
            Temizle
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium">Firma</th>
                <th className="px-3 py-2.5 text-left font-medium">Ülke</th>
                <th className="px-3 py-2.5 text-left font-medium">Hub</th>
                <th className="px-3 py-2.5 text-left font-medium">Stand No</th>
                <th className="px-3 py-2.5 text-left font-medium">Kontak</th>
                <th className="px-3 py-2.5 text-left font-medium">Sorumlu</th>
                <th className="px-3 py-2.5 text-center font-medium">Durum</th>
                <th className="px-3 py-2.5 text-left font-medium">Stand Türü</th>
                <th className="px-3 py-2.5 text-right font-medium">Yük. (cm)</th>
                <th className="px-3 py-2.5 text-right font-medium">Alan (m²)</th>
                <th className="px-3 py-2.5 text-right font-medium">Bütçe</th>
                <th className="px-3 py-2.5 text-left font-medium">Aşama</th>
                <th className="px-3 py-2.5 text-right font-medium">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading && (
                <tr>
                  <td colSpan={13} className="px-3 py-10 text-center text-zinc-500">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={13} className="px-3 py-14 text-center text-zinc-500">
                    <HardHat className="mx-auto mb-2 h-8 w-8 text-zinc-300" />
                    <div className="text-sm">Henüz kayıt yok</div>
                    <button
                      type="button"
                      onClick={openCreate}
                      className="mt-2 text-xs font-medium text-violet-600 hover:underline"
                    >
                      İlk kaydı oluştur
                    </button>
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => {
                  const assigned = userById[r.assigned_to_user_id];
                  return (
                    <tr
                      key={r.id}
                      data-testid={`construction-row-${r.id}`}
                      className="group cursor-pointer hover:bg-zinc-50"
                      onClick={() => openEdit(r)}
                    >
                      <td className="px-3 py-2.5 font-medium">
                        {r.company_name || <span className="italic text-zinc-400">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-zinc-600">{r.country || "—"}</td>
                      <td className="px-3 py-2.5 text-zinc-600">
                        {r.hub_name ? (
                          <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700">
                            {r.hub_name}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums">{r.stand_no || "—"}</td>
                      <td className="px-3 py-2.5">
                        <div className="text-zinc-900">{r.contact_name || "—"}</div>
                        {r.contact_email && (
                          <div className="text-[11px] text-zinc-500">{r.contact_email}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-zinc-600">
                        {assigned ? (
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700"
                          >
                            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-violet-600 text-[9px] font-semibold text-white">
                              {nameInitials(assigned.name)}
                            </span>
                            {assigned.name}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <StatusDot status={r.status} />
                      </td>
                      <td className="px-3 py-2.5 text-zinc-600">{r.stand_type || "—"}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-zinc-600">
                        {r.height_cm ?? "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-zinc-600">
                        {r.size_m2 ?? "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {r.stand_cost ? (
                          <span className="font-medium">
                            {fmtMoney(r.stand_cost)} {r.currency}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-zinc-600">
                        {r.stage ? (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">
                            {r.stage}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(r);
                            }}
                            data-testid={`construction-edit-${r.id}`}
                            className="rounded-md p-1 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900"
                            title="Düzenle"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          {isSuper && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                remove(r);
                              }}
                              data-testid={`construction-delete-${r.id}`}
                              disabled={deleting === r.id}
                              className="rounded-md p-1 text-red-500 hover:bg-red-50"
                              title="Sil"
                            >
                              {deleting === r.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="h-3.5 w-3.5" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit — right-side drawer (Sheet) */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-2xl"
          data-testid="construction-sheet"
        >
          <SheetHeader className="mb-4">
            <SheetTitle>
              {editingRow ? "Kaydı düzenle" : "Yeni inşaat kaydı"}
            </SheetTitle>
            <SheetDescription>
              Firma, kontak, stant ölçüleri ve bütçe bilgilerini tek panelden yönet.
            </SheetDescription>
          </SheetHeader>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Company section */}
            <div className="md:col-span-2">
              <Label>Firma</Label>
              <CompanyCombobox
                companies={companies}
                value={form.company_id}
                onChange={handleCompanyChange}
              />
            </div>
            <div>
              <Label>Firma adı</Label>
              <Input
                value={form.company_name}
                onChange={(e) => setField("company_name", e.target.value)}
                placeholder="Şişecam"
                data-testid="construction-form-company-name"
              />
            </div>
            <div>
              <Label>Ülke</Label>
              <Input
                value={form.country}
                onChange={(e) => setField("country", e.target.value)}
                placeholder="Turkey"
                data-testid="construction-form-country"
              />
            </div>

            {/* Programme */}
            <div>
              <Label>Hub</Label>
              <Select value={form.hub_name || "__none__"} onValueChange={(v) => setField("hub_name", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="construction-form-hub">
                  <SelectValue placeholder="Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Seçiniz —</SelectItem>
                  {hubOptions.map((h) => (
                    <SelectItem key={h} value={h}>{h}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stant No</Label>
              <Input
                value={form.stand_no}
                onChange={(e) => setField("stand_no", e.target.value)}
                placeholder="1, 5A, 12/B…"
                data-testid="construction-form-stand-no"
              />
            </div>

            {/* Contact person selector — filtered by chosen company. Selecting
                one auto-fills email + phone below. */}
            <div className="md:col-span-2">
              <div className="mb-1 flex items-center justify-between">
                <Label className="flex items-center gap-1.5">
                  <UserIcon className="h-3.5 w-3.5" />
                  Kontak kişi
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAddPersonOpen(true)}
                  data-testid="construction-form-add-person-btn"
                  className="h-7 gap-1 border-zinc-300 text-[11px]"
                  title="Yeni kişi ekle (seçili firmaya bağlanır)"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  + Yeni Kişi
                </Button>
              </div>
              <Select
                value={form.contact_id || "__none__"}
                onValueChange={handleContactChange}
                disabled={personsForCurrentCompany.length === 0 && !form.company_id}
              >
                <SelectTrigger data-testid="construction-form-contact-select">
                  <SelectValue
                    placeholder={
                      form.company_id
                        ? (personsForCurrentCompany.length === 0
                          ? "Bu firmaya bağlı kişi yok — yukarıdan ekleyebilirsin"
                          : "Seçiniz")
                        : "Önce firma seç"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Seçiniz —</SelectItem>
                  {personsForCurrentCompany.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {personDisplayName(p)}
                      {p.title ? ` · ${p.title}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.company_id && personsForCurrentCompany.length === 0 && (
                <p className="mt-1 text-[11px] text-amber-700">
                  Bu firmaya bağlı henüz kontak kişi yok. Yukarıdaki &quot;+ Yeni Kişi&quot;
                  butonu ile hızlıca ekleyebilirsin.
                </p>
              )}
            </div>
            <div>
              <Label>Kontak adı</Label>
              <Input
                value={form.contact_name}
                onChange={(e) => setField("contact_name", e.target.value)}
                placeholder="Kaan Gençceylan"
                data-testid="construction-form-contact-name"
              />
            </div>
            <div>
              <Label>Kontak e-posta</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) => setField("contact_email", e.target.value)}
                placeholder="ornek@firma.com"
                data-testid="construction-form-contact-email"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Kontak telefon</Label>
              <Input
                value={form.contact_phone}
                onChange={(e) => setField("contact_phone", e.target.value)}
                placeholder="+90 555 000 0000"
                data-testid="construction-form-contact-phone"
              />
            </div>

            <div>
              <Label>Sorumlu (Assigned To)</Label>
              <Select
                value={form.assigned_to_user_id || "__none__"}
                onValueChange={(v) =>
                  setField("assigned_to_user_id", v === "__none__" ? null : v)
                }
              >
                <SelectTrigger data-testid="construction-form-assigned-select">
                  <SelectValue placeholder="Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Seçiniz —</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger data-testid="construction-form-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>
                      <span className="flex items-center gap-2">
                        <span
                          style={{ backgroundColor: m.color }}
                          className="inline-block h-2.5 w-2.5 rounded-full"
                        />
                        {m.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Stand spec */}
            <div>
              <Label>Stand türü</Label>
              <Select
                value={form.stand_type || "__none__"}
                onValueChange={(v) => setField("stand_type", v === "__none__" ? "" : v)}
              >
                <SelectTrigger data-testid="construction-form-stand-type">
                  <SelectValue placeholder="Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Seçiniz —</SelectItem>
                  {STAND_TYPES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Aşama (Stage)</Label>
              <Select value={form.stage || "__none__"} onValueChange={(v) => setField("stage", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="construction-form-stage">
                  <SelectValue placeholder="Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Seçiniz —</SelectItem>
                  {STAGE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Yükseklik (cm)</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={form.height_cm}
                onChange={(e) => setField("height_cm", e.target.value)}
                placeholder="360"
                data-testid="construction-form-height"
              />
            </div>
            <div>
              <Label>Alan (m²)</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={form.size_m2}
                onChange={(e) => setField("size_m2", e.target.value)}
                placeholder="75"
                data-testid="construction-form-size"
              />
            </div>
            <div>
              <Label>Bütçe</Label>
              <Input
                inputMode="numeric"
                value={formatMoneyDisplay(form.stand_cost)}
                onChange={(e) => setField("stand_cost", parseMoneyDigits(e.target.value))}
                placeholder="250.000"
                data-testid="construction-form-cost"
              />
            </div>
            <div>
              <Label>Para birimi</Label>
              <Select value={form.currency} onValueChange={(v) => setField("currency", v)}>
                <SelectTrigger data-testid="construction-form-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label>Notlar</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                rows={3}
                placeholder="Kurulumu kendileri yapıyor…"
                data-testid="construction-form-notes"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2 border-t border-zinc-100 pt-4">
            <Button variant="ghost" onClick={() => setSheetOpen(false)}>
              İptal
            </Button>
            <Button onClick={submit} disabled={saving} data-testid="construction-form-submit">
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {editingRow ? "Kaydet" : "Oluştur"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Sibling AddContactDialog — spawned by the "+ Yeni Kişi" button
          inside the sheet. Preselects the currently-chosen company so the
          new person is linked in one save, then we auto-select it in the
          Kontak dropdown so users don't have to click twice. */}
      <AddContactDialog
        open={addPersonOpen}
        onOpenChange={setAddPersonOpen}
        initialKind="person"
        companies={companies}
        people={persons}
        defaultCompanyId={form.company_id || null}
        onCreated={(created) => {
          if (!created) return;
          // Add to local persons list so the dropdown includes it immediately
          // without a full refetch.
          setPersons((prev) => [created, ...prev]);
          // Auto-select the freshly-created person + snapshot their contact
          // details onto the current form.
          setForm((prev) => ({
            ...prev,
            contact_id: created.id,
            contact_name: personDisplayName(created),
            contact_email: pickPrimary(created.email || created.emails),
            contact_phone: pickPrimary(created.phone || created.phones),
            // If no company was chosen yet, adopt the created person's
            // company link (AddContactDialog would have set one).
            company_id: prev.company_id || created.company_id || null,
          }));
        }}
      />
    </div>
  );
}
